// LIBRERIE/IMPORTS
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faFacebookF, faInstagram, faTwitter, faLinkedin, faPinterest } from '@fortawesome/free-brands-svg-icons';
import { faAngleDoubleUp, faCopyright, faMapMarkerAlt, faMobileAlt, faPhone } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from "react-i18next";
import { Link } from 'react-router-dom';

// CONFIGURAZIONI
import { AGENCY_ID, REALIZATION_YEAR } from "../../config/appConfig";

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

interface AgencyDataState {
  whatsapp?: string;
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  pinterest?: string;
  phone1?: string;
  phone2?: string;
  mobile?: string;
  mobile2?: string;
  internationalPrefix?: string;
  ragioneSociale?: string;
  vat?: string;
  widgetExternalLinks?: string;
  address_details?: {
    address: string;
    city_name: string;
    city_cap: string;
    province_name: string;
    sigla: string;
  }
}

const Footer: React.FC = () => {
  const [agencyData, setAgencyData] = useState<AgencyDataState>({
    address_details: {
      address: '',
      city_name: '',
      city_cap: '',
      province_name: '',
      sigla: '',
    }
  });

  const { t } = useTranslation();

  const scrollToTop = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchAgencyData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/agency`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agencyId: AGENCY_ID }),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data && data.data) {
          setAgencyData(data.data);
        } else {
          console.error("Formato dati agenzia inatteso:", data);
        }
      } catch (error) {
        console.error("Errore nel fetch dei dati agenzia:", error);
      }
    };
    fetchAgencyData();
  }, []);

  return (
    <footer className="footer bg-dark space-pt">
      <div className="container">
        <div className="row">
          <div className="col-lg-4 col-md-6 col-sm-12 mb-4 mb-lg-0">
            <div className="footer-contact-info">
              <h5 className="text-gold mb-4">{t('footer.aboutUsSectionTitle')}</h5>
              <p className="mb-0 text-white">{t('homePage.aboutUsSnippet.text1')}</p>
              <p className="mb-0 text-white">{t('homePage.aboutUsSnippet.text2')}</p>
            </div>
            <br />
            <div className="social d-inline-block">
              <ul className="list-unstyled d-flex flex-wrap">
                {agencyData.whatsapp && (
                  <li className="mr-3 mb-2">
                    <a
                      title={`${t('footer.social.whatsappTitle')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-white"
                      href={`https://api.whatsapp.com/send?phone=${agencyData.whatsapp.replace(/\D/g, '')}`}
                    >
                      <FontAwesomeIcon icon={faWhatsapp} size="lg" />
                    </a>
                  </li>
                )}
                {agencyData.facebook && (
                  <li className="mr-3 mb-2">
                    <a href={agencyData.facebook} target="_blank" rel="noreferrer" title={`${t('footer.social.facebookTitle')}`} className="text-white">
                      <FontAwesomeIcon icon={faFacebookF} size="lg" />
                    </a>
                  </li>
                )}
                {agencyData.instagram && (
                  <li className="mr-3 mb-2">
                    <a href={agencyData.instagram} target="_blank" rel="noreferrer" title={`${t('footer.social.instagramTitle')}`} className="text-white">
                      <FontAwesomeIcon icon={faInstagram} size="lg" />
                    </a>
                  </li>
                )}
                {agencyData.twitter && (
                  <li className="mr-3 mb-2">
                    <a href={agencyData.twitter} target="_blank" rel="noreferrer" title={`${t('footer.social.twitterTitle')}`} className="text-white">
                      <FontAwesomeIcon icon={faTwitter} size="lg" />
                    </a>
                  </li>
                )}
                {agencyData.linkedin && (
                  <li className="mr-3 mb-2">
                    <a href={agencyData.linkedin} target="_blank" rel="noreferrer" title={`${t('footer.social.linkedinTitle')}`} className="text-white">
                      <FontAwesomeIcon icon={faLinkedin} size="lg" />
                    </a>
                  </li>
                )}
                {agencyData.pinterest && (
                  <li className="mr-3 mb-2">
                    <a href={agencyData.pinterest} target="_blank" rel="noreferrer" title={`${t('footer.social.pinterestTitle')}`} className="text-white">
                      <FontAwesomeIcon icon={faPinterest} size="lg" />
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 col-sm-12 mb-4 mb-lg-0">
            <div className="footer-contact-info">
              <h5 className="text-gold mb-4">{t('footer.contactSectionTitle')}</h5>
              <ul className="list-unstyled mb-0 text-white">

                {/* Indirizzo */}
                {agencyData.address_details?.address && agencyData.address_details?.city_name && (
                  <li className="mb-2 d-flex">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 mt-1" style={{ width: "16px" }} />
                    <span>
                      {agencyData.address_details.address} <br />
                      {`${agencyData.address_details.city_cap || ''} ${agencyData.address_details.city_name}`}
                      {agencyData.address_details.province_name !== agencyData.address_details.city_name ? ` (${agencyData.address_details.sigla})` : ''}
                    </span>
                  </li>
                )}

                {/* Telefono 1 */}
                {agencyData.phone1 && (
                  <li className="mb-2 d-flex">
                    <FontAwesomeIcon icon={faPhone} className="mr-2 mt-1" style={{ width: "16px" }} />
                    <span>
                      <a href={`tel:${agencyData.internationalPrefix || '+39'}${agencyData.phone1}`} title={`${t('footer.contact.phoneTitle')}`}>
                        {`${agencyData.internationalPrefix || '+39'} ${agencyData.phone1}`}
                      </a>
                    </span>
                  </li>
                )}

                {/* Telefono 2 */}
                {agencyData.phone2 && (
                  <li className="mb-2 d-flex">
                    <FontAwesomeIcon icon={faPhone} className="mr-2 mt-1" style={{ width: "16px" }} />
                    <span>
                      <a href={`tel:${agencyData.internationalPrefix || '+39'}${agencyData.phone2}`} title={`${t('footer.contact.phoneTitle')}`}>
                        {`${agencyData.internationalPrefix || '+39'} ${agencyData.phone2}`}
                      </a>
                    </span>
                  </li>
                )}

                {/* Mobile 1 */}
                {agencyData.mobile && (
                  <li className="mb-2 d-flex">
                    <FontAwesomeIcon icon={faMobileAlt} className="mr-2 mt-1" style={{ width: "16px" }} />
                    <span>
                      <a href={`tel:${agencyData.internationalPrefix || '+39'}${agencyData.mobile}`} title={`${t('footer.contact.mobileTitle')}`}>
                        {`${agencyData.internationalPrefix || '+39'} ${agencyData.mobile}`}
                      </a>
                    </span>
                  </li>
                )}

                {/* Mobile 2 */}
                {agencyData.mobile2 && (
                  <li className="mb-2 d-flex">
                    <FontAwesomeIcon icon={faMobileAlt} className="mr-2 mt-1" style={{ width: "16px" }} />
                    <span>
                      <a href={`tel:${agencyData.internationalPrefix || '+39'}${agencyData.mobile2}`} title={`${t('footer.contact.mobileTitle')}`}>
                        {`${agencyData.internationalPrefix || '+39'} ${agencyData.mobile2}`}
                      </a>
                    </span>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Colonna Link Utili */}
          <div className="col-lg-4 col-md-6 col-sm-12 mb-4 mb-lg-0">
            <div className="footer-link">
              <h5 className="text-gold mb-4">{t('footer.linksSectionTitle')}</h5>
              <ul className="list-unstyled mb-3">
                {/* Link per navigazione interna */}
                <li><Link to="/" title={`${t('navigation.homeLinkTitle')}`}>{t('navigation.homeLinkTitle')}</Link></li>
                <li><Link to="/immobili" title={`${t('navigation.propertiesLinkTitle')}`}>{t('navigation.propertiesLinkTitle')}</Link></li>
                <li><Link to="/servizi" title={`${t('navigation.servicesLinkTitle')}`}>{t('navigation.servicesLinkTitle')}</Link></li>
                <li><Link to="/chi-siamo" title={`${t('navigation.aboutUsLinkTitle')}`}>{t('navigation.aboutUsLinkTitle')}</Link></li>
                <li><Link to="/contatti" title={`${t('navigation.contactUsLinkTitle')}`}>{t('navigation.contactUsLinkTitle')}</Link></li>
              </ul>
              {agencyData.widgetExternalLinks && (
                <div className="external-links-widget text-white mb-3" dangerouslySetInnerHTML={{ __html: agencyData.widgetExternalLinks }}></div>
              )}
              {/* Link Casagest24 */}
              <ul className='list-unstyled'>
                <li>
                  <a href="https://www.casagest24.it" title={`${t('footer.titleCasaGest24Link')}`} target='_blank' rel="noreferrer" >
                    {t('footer.externalLinks.casagest24')}
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="row align-items-center">
            {/* Info Legali */}
            <div className="col-md-5 text-center text-md-left">
              {agencyData.ragioneSociale ? (
                <p className="mb-1 text-white">
                  {agencyData.ragioneSociale} {agencyData.vat ? `- ${t('common.partitaIVA')} ${agencyData.vat}` : ''}
                </p>
              ) : agencyData.vat ? (
                <p className="mb-1 text-white">
                  {`${t('common.partitaIVA')} ${agencyData.vat}`}
                </p>
              ) : null}

              <Link to="/dati" title={`${t('footer.datiSocietariLinkTitle')}`} >{t('footer.datiSocietariLinkTitle')}</Link>
              {' | '}
              <Link to="/privacy" title={`${t('footer.privacyLinkTitle')}`} >{t('footer.privacyLinkTitle')}</Link>
              {' | '}
              <Link to="/cookies" title={`${t('footer.cookiesLinkTitle')}`}>{t('footer.cookiesLinkTitle')}</Link>

            </div>
            {/* Back to Top */}
            <div className="col-md-2 text-center mb-3 mb-md-0">
              <a id="back-to-top" className="back-to-top" title={`${t('footer.backToTopTitle')}`} onClick={scrollToTop} style={{ cursor: 'pointer' }}>
                <FontAwesomeIcon icon={faAngleDoubleUp} size="lg" />
              </a>
            </div>
            {/* Copyright */}
            <div className="col-md-5 text-center text-md-right">
              <p className="mb-0 text-white">
                <FontAwesomeIcon icon={faCopyright} />
                {' '}
                {REALIZATION_YEAR}
                {' '}
                <a href="https://www.gruppoinsieme.it" title={`${t('footer.copyrightAgencyTitle')}`} target="_blank" rel="noopener noreferrer">
                  Insieme srl. 
                </a>
                {' '}
                {t('footer.allRightsReserved')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;